/**
 * useSendingIdentityWebSocket
 *
 * Suscribe al WebSocket /ws/communications/ y, cuando llega un evento
 * sending_identity_status (push del backend desde FSM transitions),
 * invalida el cache de React Query para que la UI se refresque sin polling.
 *
 * El backend emite a:
 *   - group company_{company_id} (auto-join al conectar si user.company_id existe)
 *   - group platform_admin       (auto-join si user.is_staff)
 *
 * El consumer ya hace el group_add en connect(), aca solo escuchamos onMessage.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useWebSocket, type WebSocketStatus } from '@/shared/hooks/useWebSocket';

interface SendingIdentityStatusEvent {
  type: 'sending_identity_status';
  data: {
    identity_id: string;
    from_email: string;
    domain: string;
    status: string;
    is_default: boolean;
    is_platform_default: boolean;
    verified_at: string | null;
  };
}

interface UseSendingIdentityWebSocketOptions {
  enabled?: boolean;
  /** Callback opcional para reaccionar al evento (ej: mostrar toast). */
  onStatusChange?: (data: SendingIdentityStatusEvent['data']) => void;
}

export function useSendingIdentityWebSocket(
  { enabled = true, onStatusChange }: UseSendingIdentityWebSocketOptions = {},
): { status: WebSocketStatus } {
  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (raw: unknown) => {
      const msg = raw as Partial<SendingIdentityStatusEvent>;
      if (msg?.type !== 'sending_identity_status' || !msg.data) return;

      // Invalida lista y detalle. React Query hace refetch automatico.
      queryClient.invalidateQueries({ queryKey: ['sending-identities'] });
      queryClient.invalidateQueries({
        queryKey: ['sending-identities', msg.data.identity_id],
      });

      onStatusChange?.(msg.data);
    },
    [queryClient, onStatusChange],
  );

  const { status } = useWebSocket('/ws/communications/', {
    enabled,
    onMessage: handleMessage,
  });

  return { status };
}
